import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView } from 'framer-motion'
import {
  ClipboardClock,
  GitBranch,
  Hourglass,
  MessageSquarePlus,
  Timer,
} from 'lucide-react'

import { cn } from '@/utils'

import { containerVariants, DetailDrawerType } from '../../constants'
import { useProjectLevel } from '../../hooks/useProjectLevel'
import { useDashboardStore } from '../../store'
import { openCommentPath } from '../../utils'
import { CompletionRate } from '../CompletionRate'
import { MetricCard } from '../MetricCard'

export const ProjectLevel = () => {
  const { t } = useTranslation()
  const { filters, setDisplayedDrawer } = useDashboardStore()
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px 0px' })

  const {
    commentDensityMetric,
    isLoadingCommentDensity,
    lateDeliverablesMetric,
    isLoadingLateDeliverables,
    resolutionRate,
    openCommentsCount,
    isLoadingResolutionMetrics,
    avgCycleTimeMetric,
    isLoadingAvgCycleTime,
    approvedDeliverableSets,
    totalDeliverableSets,
  } = useProjectLevel()

  return (
    <>
      <h1 className="font-semibold text-xl text-primary-900 m-0">
        Project Dashboard
      </h1>
      <motion.div
        ref={ref}
        className="flex flex-col flex-wrap gap-5"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
      >
        <div
          className={cn(
            'space-y-5',
            'lg:flex lg:flex-wrap lg:space-y-0 lg:gap-5'
          )}
        >
          <div className="flex-1">
            <CompletionRate
              title="Completion Rate"
              current={approvedDeliverableSets}
              total={totalDeliverableSets}
              unit="deliverable sets"
            />
          </div>

          <div className="flex flex-col flex-1 gap-5">
            <MetricCard
              title={t('dashboard.metrics.lateDeliverables')}
              icon={Hourglass}
              value={lateDeliverablesMetric?.metric}
              onViewAll={() =>
                setDisplayedDrawer(DetailDrawerType.LATE_DELIVERABLES)
              }
              isLoading={isLoadingLateDeliverables}
            />
            <MetricCard
              title={t('dashboard.metrics.avgReview')}
              icon={Timer}
              isDateValue
              isDecimalValue
              isFullSizeUnit
              unit={
                avgCycleTimeMetric?.metric > 1
                  ? t('dashboard.units.days')
                  : t('dashboard.units.day')
              }
              value={avgCycleTimeMetric?.metric}
              onViewAll={() =>
                setDisplayedDrawer(DetailDrawerType.AVG_REVIEW_TIME)
              }
              isLoading={isLoadingAvgCycleTime}
            />
          </div>
        </div>
        <div
          className={cn(
            'space-y-5',
            'lg:flex lg:flex-wrap lg:space-y-0 lg:gap-5'
          )}
        >
          <MetricCard
            title={t('dashboard.metrics.resolutionRate')}
            icon={GitBranch}
            isDecimalValue
            unit="%"
            isFullSizeUnit
            value={resolutionRate}
            isLoading={isLoadingResolutionMetrics}
          />
          <MetricCard
            title={t('dashboard.metrics.openComments')}
            icon={MessageSquarePlus}
            value={openCommentsCount}
            onViewAll={
              openCommentsCount && openCommentsCount > 0
                ? () => openCommentPath({ projectId: filters.projectId })
                : undefined
            }
            isLoading={isLoadingResolutionMetrics}
          />
          <MetricCard
            title={t('dashboard.metrics.commentDensity')}
            icon={ClipboardClock}
            unit={t('dashboard.units.commentPerFile')}
            isDecimalValue
            onViewAll={() =>
              setDisplayedDrawer(DetailDrawerType.COMMENT_DENSITY)
            }
            value={commentDensityMetric?.metric}
            isLoading={isLoadingCommentDensity}
          />
        </div>
      </motion.div>
    </>
  )
}
